import {
  clampFontSizePx,
  normalizeTextLabelFontFamily,
  normalizeTextLabelFontSize,
  parseFontSizePx,
  TEXT_LABEL_PLACEHOLDER,
  TEXT_LABEL_PLACEHOLDER_COLOR,
  TEXT_LABEL_TEXT_INSET_X,
  TEXT_LABEL_TEXT_PADDING,
  type CanvasTextLabelNodeProps,
} from "@/pages/canvas/types/canvasNodeData";
import { cn } from "@/utils";
import {
  BaseEntityCardQuickChat,
  registerDashListMarkers,
  UNORDERED_LIST_WITH_DASHES,
} from "@cohesive/ui-components";
import { LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  ORDERED_LIST,
  STRIKETHROUGH,
} from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import {
  NodeResizeControl,
  ResizeControlVariant,
  useReactFlow,
  useStore,
} from "@xyflow/react";
import {
  $addUpdateTag,
  $getRoot,
  $setSelection,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  SKIP_DOM_SELECTION_TAG,
  type EditorState,
  type LexicalEditor,
} from "lexical";
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { LATTICE, snapToGrid } from "../canvas-controls.config";
import {
  selectFirstRow,
  withCanvasSelection,
} from "../components/toolbar/canvasFormatCommands";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasFloatingChat } from "../context/CanvasFloatingChatContext";
import { useCanvasProcess } from "../context/CanvasProcessContext";
import {
  useCanvasSelectionCount,
  useIsMultiSelectToolbarOpen,
} from "../context/CanvasSelectionCountContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  readLabelStyle,
  rememberLabelStyle,
  type CanvasLabelStyle,
} from "../hooks/useCanvasLabelStyle";
import { useSectionPreviewHighlight } from "../hooks/useSectionPreviewHighlight";
import { clearDomSelectionInside } from "../utils/domSelection";
import { TextLabelFloatingToolbar } from "./TextLabelFloatingToolbar";
import { CanvasConnectHandles } from "./shared/CanvasConnectHandles";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";

const MIN_WIDTH = 96;

/** Corner scale handles; the edges carry width-only line controls. */
const CORNERS = [
  "top-left",
  "top-right",
  "bottom-left",
  "bottom-right",
] as const;

type LabelCorner = (typeof CORNERS)[number];

type CornerDrag = {
  corner: LabelCorner;
  pointerId: number;
  clientX: number;
  clientY: number;
  zoom: number;
  width: number;
  height: number;
  fontPx: number;
  x: number;
  y: number;
};

const textNodeTheme = {
  paragraph: "mb-0",
  list: {
    ul: "list-disc list-inside ml-2",
    ol: "list-decimal list-inside ml-2",
    listitem: "ml-2",
  },
  text: {
    bold: "font-bold",
    strikethrough: "line-through",
    code: "bg-n-95/10 dark:bg-n-95/10 px-1 py-0.5 rounded text-xs font-mono",
  },
  link: "text-teal dark:text-teal underline hover:no-underline cursor-pointer",
};

const TRANSFORMERS = [
  BOLD_STAR,
  BOLD_UNDERSCORE,
  ITALIC_STAR,
  ITALIC_UNDERSCORE,
  BOLD_ITALIC_STAR,
  BOLD_ITALIC_UNDERSCORE,
  STRIKETHROUGH,
  UNORDERED_LIST_WITH_DASHES,
  ORDERED_LIST,
];

function serializeToMarkdown(editorState: EditorState): string {
  let markdown = "";
  editorState.read(() => {
    markdown = $convertToMarkdownString(TRANSFORMERS);
  });
  // Lexical serializes an empty paragraph as "\n" — normalize to ""
  // so content is reliably empty when the editor has no visible text.
  return markdown.trim();
}

function isEditorEmpty(editorState: EditorState): boolean {
  let empty = true;
  editorState.read(() => {
    const root = $getRoot();
    const text = root.getTextContent().trim();
    empty = text === "";
  });
  return empty;
}

function DashListPlugin() {
  const [editor] = useLexicalComposerContext();
  useEffect(() => registerDashListMarkers(editor), [editor]);
  return null;
}

function EditorRefBridge({
  editorRef,
}: {
  editorRef: React.MutableRefObject<LexicalEditor | null>;
}) {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    editorRef.current = editor;
  }, [editor, editorRef]);
  return null;
}

function InitializerPlugin({
  value,
  isFocused,
  onInitialized,
}: {
  value: string;
  isFocused: boolean;
  onInitialized: () => void;
}) {
  const [editor] = useLexicalComposerContext();
  const lastValueRef = useRef<string>("");
  const hasInitializedRef = useRef(false);

  // Remote content lands while nobody is typing here. SKIP_DOM_SELECTION_TAG
  // keeps Lexical from reconciling the import's selection into the DOM of an
  // unfocused editor (WebKit paints that as a stray highlight), and the
  // selection is dropped so the next focus starts clean.
  const importRemote = useCallback(
    (markdown: string) => {
      const rootEl = editor.getRootElement();
      const focused = !!rootEl && rootEl.contains(document.activeElement);
      editor.update(() => {
        if (!focused) $addUpdateTag(SKIP_DOM_SELECTION_TAG);
        $convertFromMarkdownString(markdown, TRANSFORMERS);
        if (!focused) $setSelection(null);
      });
    },
    [editor],
  );

  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      lastValueRef.current = value;

      if (value) importRemote(value);

      onInitialized();
      return;
    }

    // While this user is actively typing, track the latest Loro value so our
    // own debounced saves don't create a stale diff that triggers a destructive
    // re-sync when the editor eventually loses focus.
    if (isFocused) {
      lastValueRef.current = value;
      return;
    }
    if (value === lastValueRef.current) return;

    lastValueRef.current = value;
    importRemote(value || "");
  }, [value, importRemote, isFocused, onInitialized]);

  return null;
}

export const TextLabelNode = memo(function TextLabelNode({
  id,
  data,
  selected,
  dragging,
  width: rawWidth,
  positionAbsoluteX,
  positionAbsoluteY,
}: CanvasTextLabelNodeProps) {
  // `rawWidth` is React Flow's MEASURED width, so it cannot tell hug from
  // fixed. The stored width can, and it is the whole mode switch: present =
  // fixed (text wraps to it), absent = hug (the box follows the text).
  const storedWidth = useStore((s) => s.nodeLookup.get(id)?.width);
  const zoom = useStore((s) => s.transform[2]);
  const isHug = storedWidth === undefined;
  const measuredWidth = rawWidth ?? storedWidth ?? 0;
  const canvas = useCanvasApi();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { canEdit } = canvas;
  const { dispatchProcess } = useCanvasProcess();
  const { openSession: openCanvasFloatingSession } = useCanvasFloatingChat();
  const { canvasId } = useParams<{ canvasId: string }>();
  const { setNodes, getNode, getZoom } = useReactFlow();
  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setIsPopoverOpen(open);
      setToolbarPopoverOpen(open);
      // A closing menu hands the caret back, so the next character lands in
      // the label rather than nowhere.
      if (!open && isEditingRef.current) shouldAutoFocusRef.current = true;
    },
    [setToolbarPopoverOpen],
  );
  const nodeBodyRef = useRef<HTMLDivElement>(null);

  const content = data?.content || "";

  // isEditing: true  → overlay removed, ContentEditable is live, nodrag active
  // isEditing: false → overlay present, full area is draggable by React Flow
  const [isEditing, setIsEditing] = useState(selected && canEdit);
  // True only while the Lexical editor actually has browser focus — used by
  // InitializerPlugin to decide whether to sync incoming remote content.
  const [isFocused, setIsFocused] = useState(false);
  // Emptiness read off the editor, not off `data.content`: the saved content
  // is 300ms behind the keystroke, and the box has to stop reserving the
  // placeholder's width the instant the first character lands.
  const [isTextEmpty, setIsTextEmpty] = useState(!content);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChatInput, setShowChatInput] = useState(false);

  const editorRef = useRef<LexicalEditor | null>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track selected in a ref so handleBlur (stable closure) can read the
  // latest value. This prevents deleting the node when the user clicks a
  // toolbar button — the node stays selected, so we skip deletion.
  const selectedRef = useRef(selected);
  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);
  const isEditingRef = useRef(isEditing);
  isEditingRef.current = isEditing;

  // The text is contenteditable only for the edit session; the DOM attribute
  // follows editor.setEditable (ContentEditable ignores its own prop). A
  // layout effect so the attribute is in place before the rAF focus loop.
  // Leaving edit mode drops any range still anchored in the editor so WebKit
  // never paints it.
  useLayoutEffect(() => {
    editorRef.current?.setEditable(canEdit && isEditing);
    if (!isEditing) {
      clearDomSelectionInside(editorRef.current?.getRootElement());
    }
  }, [canEdit, isEditing]);

  // Auto-focus is only desired for nodes placed by THIS user.
  const shouldAutoFocusRef = useRef(selected && canEdit);
  // Snapshot of `selected` at the moment the overlay receives mousedown.
  // React Flow updates selection state during mousedown processing, so by
  // the time onClick fires `selected` is already true even for the very
  // first click. Reading it in mousedown lets us distinguish
  //   • "first click to select" (was false → don't enter edit mode)
  //   • "click on already-selected node" (was true → enter edit mode)
  const wasSelectedOnMouseDownRef = useRef(false);
  // Mouse-down screen coordinates used to distinguish click from drag when
  // deciding whether to enter edit mode.
  const mouseDownPosRef = useRef<{ x: number; y: number } | null>(null);
  // True while a resize drag is in progress — gates the ResizeObserver so it
  // doesn't call setNodes mid-resize (which kills the visual handles).
  const isResizingRef = useRef(false);
  // Left/right line drags move x when the left edge is the one being pulled;
  // the end-of-drag lattice snap has to compensate or the right edge walks.
  const resizeOriginXRef = useRef<number | null>(null);
  const resizeStartWidthRef = useRef<number | null>(null);
  // Corner scale is driven here rather than by NodeResizer: the font has to
  // scale with the box, and only then does the opposite corner actually stay
  // put — a label's height is its text's height, so a height React Flow
  // writes but the DOM ignores is exactly what made every non-bottom-right
  // corner drag move the label.
  const cornerDragRef = useRef<CornerDrag | null>(null);
  const [cornerPreview, setCornerPreview] = useState<{
    fontPx: number;
    width?: number;
  } | null>(null);

  // Auto-focus after every render until focus is confirmed.
  // No deps — re-runs after each render cycle so we don't miss the frame
  // where the editor root element becomes available and focusable.
  // requestAnimationFrame defers the focus call past any competing click /
  // canvas-event processing in the same task, making it reliably win.
  useEffect(() => {
    if (!shouldAutoFocusRef.current) return;
    const rafId = requestAnimationFrame(() => {
      const rootEl = editorRef.current?.getRootElement();
      if (!rootEl) return;
      rootEl.focus({ preventScroll: true });
      // Once the element actually has focus, stop retrying.
      if (
        document.activeElement === rootEl ||
        rootEl.contains(document.activeElement)
      ) {
        shouldAutoFocusRef.current = false;
      }
    });
    return () => cancelAnimationFrame(rafId);
  }); // intentionally no dependency array

  // Content-level halves of the sticky style (bold/italic/… ride on the node).
  // Once, on a label placed empty: the caret carries them into the first
  // character, and the placeholder is already aligned like the text will be.
  const appliedStickyFormatsRef = useRef(!!content);
  useEffect(() => {
    if (appliedStickyFormatsRef.current) return;
    const editor = editorRef.current;
    if (!editor || !isEditing) return;
    appliedStickyFormatsRef.current = true;
    const sticky = readLabelStyle();
    if (sticky.align === "left" && !sticky.is_strikethrough) return;
    withCanvasSelection(editor, selectFirstRow, () => {
      if (sticky.align !== "left") {
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, sticky.align);
      }
      if (sticky.is_strikethrough) {
        editor.dispatchCommand(FORMAT_TEXT_COMMAND, "strikethrough");
      }
    });
  }, [isEditing]);

  // Keep RF's hit-test area in sync with actual content height. A label's
  // box is never sized by the resizer — its height is whatever the text
  // needs — so without this the last written height sticks after the text
  // reflows (fewer lines after widening, taller lines after a font bump).
  //
  // Wrapped in `canvas.withoutUndo` (→ `room.history.disable`) because
  // auto-height is DERIVED state — the user didn't ask for it, the layout
  // engine emits it in response to text/width changes. Letting it land on
  // the undo stack would (a) push noise entries between real user actions
  // and (b) wipe the redo stack on every text re-flow, which made
  // Cmd+Shift+Z stop after one step.
  useEffect(() => {
    const el = nodeBodyRef.current;
    if (!el) return;
    let lastHeight = el.offsetHeight;
    const ro = new ResizeObserver(() => {
      if (isResizingRef.current) return;
      requestAnimationFrame(() => {
        if (!el.isConnected || isResizingRef.current) return;
        const measured = el.offsetHeight;
        if (measured === lastHeight) return;
        lastHeight = measured;
        canvas.withoutUndo(() =>
          setNodes((prev) =>
            prev.map((n) => (n.id === id ? { ...n, height: measured } : n)),
          ),
        );
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [id, setNodes, canvas]);

  const textColor = data?.text_color;
  const fontFamily = normalizeTextLabelFontFamily(data?.font_family);
  const storedFontPx = parseFontSizePx(data?.font_size);
  const fontPx = cornerPreview?.fontPx ?? storedFontPx;
  const fontSize = `${fontPx}px`;
  const isBold = data?.is_bold ?? false;
  const isItalic = data?.is_italic ?? false;
  const isUnderline = data?.is_underline ?? false;

  const selectedNodeCount = useCanvasSelectionCount();
  const isMultiSelectToolbarOpen = useIsMultiSelectToolbarOpen();

  // Multi-select unmounts this toolbar without Radix onOpenChange(false).
  // Clear chat + popover latches on multi-select or deselect — otherwise
  // showChatInput / isPopoverOpen alone would remount the wrong UI when
  // returning to single-select (node stays selected during multi-select).
  useEffect(() => {
    if (isMultiSelectToolbarOpen || !selected) {
      setShowChatInput(false);
      if (isPopoverOpen) {
        handlePopoverOpenChange(false);
      }
    }
  }, [
    isMultiSelectToolbarOpen,
    selected,
    isPopoverOpen,
    handlePopoverOpenChange,
  ]);

  const isSectionPreviewMember = useSectionPreviewHighlight(id);

  // Enter edit mode — only available for editors/owners.
  const handleStartEditing = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(true);
    shouldAutoFocusRef.current = true;
  }, [canEdit]);

  // Debounced save — identical pattern to TextNoteNode.
  //
  // CRITICAL: Lexical's `OnChangePlugin` fires for any editor-state change
  // including selection-only ones (clicking into the editor, moving the
  // cursor, focus shifts). Without the `markdown === current` short-circuit
  // every cursor click would issue a Liveblocks write whose only meaningful
  // diff is `updated_at`, which bumps the undo stack and wipes the user's
  // redo history. Compare against the live `data?.content` (reactive prop
  // sourced from storage) so we never write a value that's already there.
  const handleContentChange = useCallback(
    (editorState: EditorState) => {
      setIsTextEmpty(isEditorEmpty(editorState));
      if (!isInitialized) return;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        const markdown = serializeToMarkdown(editorState);
        if (markdown === (data?.content ?? "")) return;
        canvas.updateNodeData(id, {
          content: markdown,
          updated_at: new Date().toISOString(),
        });
      }, 300);
    },
    [id, canvas, isInitialized, data?.content],
  );

  useEffect(() => setIsTextEmpty(!content), [content]);

  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  // On blur: exit edit mode, and a label with no text stops existing —
  // Escape and click-away both land here, so an empty label is never left
  // behind. Focus moving into this node's own chrome (the font-size field, a
  // colour popover) is not a blur at all: the label is still being authored,
  // and ending the session there is what used to collapse an empty one the
  // moment its formatting was changed.
  const handleBlur = useCallback(
    (next: HTMLElement | null) => {
      if (
        next?.closest('[role="toolbar"], [data-radix-popper-content-wrapper]')
      ) {
        return;
      }
      setIsEditing(false);
      setIsFocused(false);
      if (!editorRef.current) return;
      if (canEdit && isEditorEmpty(editorRef.current.getEditorState())) {
        canvas.removeNode(id);
      }
    },
    [id, canvas, canEdit],
  );

  // Every formatting change is also the formatting the next label is placed
  // in — same idea as the toolbar remembering the last shape style.
  const applyFormat = useCallback(
    (patch: Partial<CanvasLabelStyle>) => {
      rememberLabelStyle(patch);
      const { is_strikethrough, align, ...nodeFields } = patch;
      if (Object.keys(nodeFields).length > 0) {
        canvas.updateNodeData(id, nodeFields);
      }
    },
    [id, canvas],
  );

  const handleTextColorChange = useCallback(
    (color: string) => {
      applyFormat({ text_color: color === "default" ? null : color });
    },
    [applyFormat],
  );

  const handleFontFamilyChange = useCallback(
    (newFontFamily: string) => {
      applyFormat({ font_family: normalizeTextLabelFontFamily(newFontFamily) });
    },
    [applyFormat],
  );

  const handleFontSizeChange = useCallback(
    (newFontSize: string) => {
      applyFormat({ font_size: normalizeTextLabelFontSize(newFontSize) });
    },
    [applyFormat],
  );

  const syncMeasuredHeight = useCallback(() => {
    const el = nodeBodyRef.current;
    if (!el) return;
    const measured = el.offsetHeight;
    canvas.withoutUndo(() =>
      setNodes((prev) =>
        prev.map((n) => (n.id === id ? { ...n, height: measured } : n)),
      ),
    );
  }, [id, canvas, setNodes]);

  // Corner scale: box and font together, opposite corner pinned. Position is
  // written live (undo paused for the gesture) so the pinned corner holds on
  // screen; the font rides local state until release so the drag isn't a
  // storage write per frame.
  const handleCornerPointerDown = useCallback(
    (e: React.PointerEvent, corner: LabelCorner) => {
      if (!canEdit) return;
      e.stopPropagation();
      e.preventDefault();
      const el = nodeBodyRef.current;
      const node = getNode(id);
      if (!el || !node) return;

      isResizingRef.current = true;
      cornerDragRef.current = {
        corner,
        pointerId: e.pointerId,
        clientX: e.clientX,
        clientY: e.clientY,
        zoom: getZoom(),
        width: el.offsetWidth,
        height: el.offsetHeight,
        fontPx: storedFontPx,
        x: node.position.x,
        y: node.position.y,
      };
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      canvas.pauseUndo();
    },
    [id, canEdit, canvas, getNode, getZoom, storedFontPx],
  );

  const cornerScaleAt = useCallback(
    (drag: CornerDrag, e: React.PointerEvent) => {
      const pullsLeft = drag.corner.endsWith("left");
      const pullsUp = drag.corner.startsWith("top");
      const dx = (e.clientX - drag.clientX) / drag.zoom;
      const dy = (e.clientY - drag.clientY) / drag.zoom;
      const nextW = drag.width + (pullsLeft ? -dx : dx);
      const nextH = drag.height + (pullsUp ? -dy : dy);
      // Diagonal drags follow whichever axis was pulled further, matching
      // React Flow's own aspect-locked resize.
      const raw = Math.max(nextW / drag.width, nextH / drag.height);
      const scale = clampFontSizePx(drag.fontPx * raw) / drag.fontPx;
      return { pullsLeft, pullsUp, scale };
    },
    [],
  );

  const handleCornerPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const drag = cornerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const { pullsLeft, pullsUp, scale } = cornerScaleAt(drag, e);
      const width = drag.width * scale;
      const height = drag.height * scale;

      setCornerPreview({
        fontPx: clampFontSizePx(drag.fontPx * scale),
        width: isHug ? undefined : width,
      });
      canvas.updateNodePosition(id, {
        x: pullsLeft ? drag.x + drag.width - width : drag.x,
        y: pullsUp ? drag.y + drag.height - height : drag.y,
      });
    },
    [id, canvas, cornerScaleAt, isHug],
  );

  const handleCornerPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const drag = cornerDragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      cornerDragRef.current = null;
      isResizingRef.current = false;

      const { pullsLeft, pullsUp, scale } = cornerScaleAt(drag, e);
      // The DOM still holds the preview, so this is the box the committed
      // font actually produces — the predicted one is always a little off
      // (padding doesn't scale, glyph advances round), and placing the
      // pinned corner from a prediction is what makes it wander.
      const el = nodeBodyRef.current;
      const measured = el?.offsetWidth ?? drag.width * scale;
      const settledHeight = el?.offsetHeight ?? drag.height * scale;
      // A fixed label lands on the lattice, same as every other node resize
      // on this canvas; a hug label has no width to land anywhere.
      const settledWidth = isHug
        ? measured
        : Math.max(MIN_WIDTH, snapToGrid(measured, LATTICE));
      const fontPx =
        cornerPreview?.fontPx ?? clampFontSizePx(drag.fontPx * scale);
      setCornerPreview(null);

      canvas.updateNodeData(id, {
        font_size: `${fontPx}px`,
        updated_at: new Date().toISOString(),
      });
      // A fixed label keeps its wrap layout through the scale: the width grows
      // by the same factor as the font, so the lines break where they did. A
      // hug label stays hug — it has no width to write.
      if (!isHug) {
        canvas.updateNodeDimensions(id, { width: settledWidth });
      }
      canvas.updateNodePosition(id, {
        x: pullsLeft ? drag.x + drag.width - settledWidth : drag.x,
        y: pullsUp ? drag.y + drag.height - settledHeight : drag.y,
      });
      canvas.resumeUndo();
      requestAnimationFrame(syncMeasuredHeight);
    },
    [id, canvas, cornerScaleAt, cornerPreview, isHug, syncMeasuredHeight],
  );

  const handleBoldChange = useCallback(() => {
    applyFormat({ is_bold: !isBold });
  }, [applyFormat, isBold]);

  const handleItalicChange = useCallback(() => {
    applyFormat({ is_italic: !isItalic });
  }, [applyFormat, isItalic]);

  const handleUnderlineChange = useCallback(() => {
    applyFormat({ is_underline: !isUnderline });
  }, [applyFormat, isUnderline]);

  const handleChatSubmit = useCallback(
    async (message: string) => {
      const content = data?.content || "";
      const flowSessionId = await dispatchProcess({
        nodeId: id,
        referencedEntities: [
          {
            entity_id: id,
            entity_type: "canvas_text_label",
            name: content.slice(0, 60) || "Text Label",
            platform: "cohesive",
          },
        ],
        action: "chat",
        customPrompt: message,
      });

      setShowChatInput(false);

      if (flowSessionId && canvasId) {
        openCanvasFloatingSession(flowSessionId);
      }
    },
    [id, data?.content, dispatchProcess, canvasId, openCanvasFloatingSession],
  );

  const initialConfig = useMemo(
    () => ({
      namespace: `TextLabelNode-${id}`,
      nodes: [ListNode, ListItemNode, LinkNode],
      onError: (error: Error) => {
        console.error("Lexical error in TextLabelNode:", error);
      },
      theme: textNodeTheme,
      // Set the correct initial editability so viewers never see an editable
      // editor on first paint. The useEffect above keeps it in sync on changes.
      editable: canEdit && isEditing,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id], // canEdit/isEditing intentionally excluded: LexicalComposer reads
    // initialConfig only on mount; changes are handled by setEditable in the effect.
  );

  // Toolbar follows selection only. Do not keep it mounted via isPopoverOpen /
  // isEditing after deselection — nested Format ▸ Color stacks used that latch
  // to survive canvas clicks. Popover open still notifies CanvasUi so RF can
  // ignore stray select:false while interacting with portaled menus.
  // Suppress when MultiSelectFloatingToolbar is mounted (multiSelectNodes >= 2).
  const showToolbar =
    canEdit &&
    !dragging &&
    !isMultiSelectToolbarOpen &&
    selected &&
    selectedNodeCount === 1;

  // Hug leaves the box unsized so it shrink-wraps the text; fixed pins it to
  // the stored width.
  const boxWidth = cornerPreview?.width ?? storedWidth;
  // Pristine: no character has been typed yet. The prompt stands in for the
  // content and the box hugs it — restyling the label restyles the prompt,
  // and only the first character ends the state.
  const showPlaceholder = canEdit && isTextEmpty;

  // The prompt is the content until there is content: same face, size and
  // weight, so nothing about the text changes when it takes over.
  const promptStyle = {
    fontFamily,
    fontSize,
    fontWeight: isBold ? ("bold" as const) : ("normal" as const),
    fontStyle: isItalic ? ("italic" as const) : ("normal" as const),
    textDecoration: isUnderline ? "underline" : "none",
    whiteSpace: "pre" as const,
  };

  // Compute effective text color style
  const textColorStyle =
    textColor && textColor !== "default" ? textColor : undefined;

  return (
    <div
      ref={nodeBodyRef}
      className={cn(
        "relative",
        isSectionPreviewMember && "ring-2 ring-canvas-selection/60 rounded-lg",
      )}
      style={{ width: boxWidth }}
      // Hug never wraps: the only line breaks are the ones the author typed.
      // Lexical stamps white-space onto its own root element, so the rule
      // lives in CSS (canvas-styles.css) where it can outrank that.
      data-canvas-label-mode={isHug ? "hug" : "fixed"}
    >
      {/* Floating Toolbar — appears on select, no hover trigger */}
      {showToolbar && (
        <CanvasFloatingToolbarPortal
          positionAbsoluteX={positionAbsoluteX}
          positionAbsoluteY={positionAbsoluteY}
          width={measuredWidth}
          topOffset={64}
        >
          {showChatInput ? (
            <BaseEntityCardQuickChat
              visible={true}
              onSubmit={handleChatSubmit}
              onClose={() => setShowChatInput(false)}
            />
          ) : (
            <TextLabelFloatingToolbar
              visible={true}
              textColor={textColor}
              fontFamily={fontFamily}
              fontSize={fontSize}
              isBold={isBold}
              isItalic={isItalic}
              isUnderline={isUnderline}
              onTextColorChange={handleTextColorChange}
              onFontFamilyChange={handleFontFamilyChange}
              onFontSizeChange={handleFontSizeChange}
              onBoldChange={handleBoldChange}
              onItalicChange={handleItalicChange}
              onUnderlineChange={handleUnderlineChange}
              onChatClick={() => setShowChatInput(true)}
              editor={editorRef.current}
              onPopoverOpenChange={handlePopoverOpenChange}
              onContentFormatChange={applyFormat}
            />
          )}
        </CanvasFloatingToolbarPortal>
      )}

      {/* One bounding box for both states — the same rectangle whether the
          label is being edited or merely selected. Inset by a pixel so its
          2px stroke lands exactly where the edge resize lines do. */}
      {(selected || isEditing) && (
        <div className="pointer-events-none absolute -inset-px rounded-[3px] border-2 border-canvas-selection/60" />
      )}

      {/* Resize affordances — selected, not editing, editors only. Left and
          right set an explicit width (the label flips to fixed and the text
          wraps); the corners scale box and font together. There is nothing
          on the top and bottom edges: a label's height is its text's.
          z-20 clears the drag overlay, which otherwise swallows the inner
          half of every grip. */}
      {selected && !isEditing && canEdit && (
        <>
          {(["left", "right"] as const).map((edge) => (
            <NodeResizeControl
              key={edge}
              className="z-20"
              position={edge}
              variant={ResizeControlVariant.Line}
              resizeDirection="horizontal"
              minWidth={MIN_WIDTH}
              onResizeStart={(_evt, params) => {
                isResizingRef.current = true;
                resizeOriginXRef.current = params.x;
                resizeStartWidthRef.current = params.width;
              }}
              onResizeEnd={(_evt, params) => {
                isResizingRef.current = false;
                const startWidth = resizeStartWidthRef.current;
                resizeStartWidthRef.current = null;
                // A click on the edge is a zero-distance drag. Committing its
                // width would turn a hugging label fixed just by clicking —
                // and would land before the double-click that asks for hug.
                if (params.width === startWidth) {
                  resizeOriginXRef.current = null;
                  return;
                }
                // Settle the width on the lattice — drag deltas are relative,
                // so a legacy off-lattice width keeps its residue otherwise.
                // Pulling the left edge moved x, so compensate there or the
                // right edge walks with the snap.
                const width = Math.max(
                  MIN_WIDTH,
                  snapToGrid(params.width, LATTICE),
                );
                const originX = resizeOriginXRef.current;
                resizeOriginXRef.current = null;
                if (originX !== null && params.x !== originX) {
                  canvas.updateNodePosition(id, {
                    x: params.x + params.width - width,
                    y: params.y,
                  });
                }
                canvas.updateNodeDimensions(id, { width });
                syncMeasuredHeight();
              }}
            >
              {/* Double-click the side to go back to hugging: the stored
                  width is dropped, not set to a measurement. The control
                  itself takes no DOM props, so the gesture rides on a child
                  covering it — drag events still bubble up to the resizer.
                  The negative inset matches the line control's own grab
                  padding (its content box is zero wide between the borders),
                  and z-10 clears the ::after that paints over children. */}
              <div
                className="absolute -inset-1 z-10"
                onDoubleClick={(event) => {
                  event.stopPropagation();
                  // Unconditional: the render's isHug is a frame behind the
                  // resize-end that may just have written a width, so the
                  // gesture clears whatever is stored either way.
                  canvas.clearNodeWidth(id);
                  // The wrapped height outlives the width it came from.
                  requestAnimationFrame(syncMeasuredHeight);
                }}
              />
            </NodeResizeControl>
          ))}
          {CORNERS.map((corner) => (
            <div
              key={corner}
              className={cn(
                "react-flow__resize-control nodrag handle z-20",
                corner.split("-").join(" "),
              )}
              style={{ scale: `${Math.max(1 / zoom, 1)}` }}
              onPointerDown={(e) => handleCornerPointerDown(e, corner)}
              onPointerMove={handleCornerPointerMove}
              onPointerUp={handleCornerPointerUp}
              onPointerCancel={handleCornerPointerUp}
            />
          ))}
        </>
      )}

      <CanvasConnectHandles />

      {/* Node body — transparent background, height auto-expands with content */}
      <div className="relative rounded min-h-[32px]">
        {/* The prompt is painted by Lexical's absolutely-positioned
            placeholder, which can't size anything. This zero-height twin
            carries the same text through layout, so an empty box hugs the
            prompt with the same inset on both sides. */}
        {showPlaceholder && (
          <div
            aria-hidden
            className="h-0 overflow-hidden"
            style={{ ...promptStyle, paddingInline: TEXT_LABEL_TEXT_INSET_X }}
          >
            {TEXT_LABEL_PLACEHOLDER}
          </div>
        )}
        {/*
          Interaction overlay — present whenever the node is NOT in edit mode.

          Cursor behaviour:
            • Not selected → cursor-grab  (whole area drags / selects)
            • Selected     → cursor-grab  (same cursor until edit mode is active)

          Click/drag state machine:
            • mousedown records whether the node was ALREADY selected at that
              instant (before React Flow processes the event).
            • click enters edit mode only when the node was already selected at
              mousedown. Browsers suppress click after drags, so drag-to-move
              still works naturally.
            • mouseup still checks movement threshold to swallow drag-release
              side-effects (pane deselect paths).

          This means:
            1st click on unselected  → select only (toolbar + handles appear)
            click while selected     → enter edit mode
            drag while selected      → move node (overlay has no nodrag)
        */}
        {!isEditing && (
          <div
            className={cn(
              "absolute inset-0 z-10 rounded",
              canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default",
            )}
            onPointerDown={(e) => {
              wasSelectedOnMouseDownRef.current = selected ?? false;
              mouseDownPosRef.current = { x: e.clientX, y: e.clientY };

              // React Flow uses pointerdown (not mousedown) to initiate drag
              // and calls setPointerCapture, which redirects ALL subsequent
              // pointer/mouse events to RF's element. Stopping propagation at
              // the pointer-event level is the only way to prevent RF from
              // capturing the pointer and swallowing our pointerup.
              //
              // Only do this when already selected — unselected nodes need the
              // event to propagate so RF handles selection + drag.
              if (selected) {
                e.stopPropagation();
              }
            }}
            onPointerUp={(e) => {
              if (
                !wasSelectedOnMouseDownRef.current ||
                !mouseDownPosRef.current
              ) {
                mouseDownPosRef.current = null;
                return;
              }

              const dx = Math.abs(e.clientX - mouseDownPosRef.current.x);
              const dy = Math.abs(e.clientY - mouseDownPosRef.current.y);
              const moved = dx > 3 || dy > 3;
              mouseDownPosRef.current = null;

              if (moved) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }

              handleStartEditing();
            }}
          />
        )}

        <LexicalComposer initialConfig={initialConfig}>
          <EditorRefBridge editorRef={editorRef} />
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={cn(
                  "outline-none min-h-[32px]",
                  // nodrag/nowheel only while editing — lets Lexical handle
                  // text selection without React Flow intercepting the drag.
                  isEditing && "nodrag",
                  isEditing && "nowheel",
                )}
                onKeyDown={(e) => {
                  if (e.key === "Tab") e.preventDefault();
                  e.stopPropagation();
                  if (e.key === "Escape") {
                    // Focus the React Flow root instead of calling .blur()
                    // directly. If we just blur(), the browser forwards focus
                    // to the nearest other contenteditable in DOM order (e.g.
                    // a neighbouring TextNoteNode), triggering its onFocus and
                    // showing its toolbar. Focusing the RF root (tabIndex=0)
                    // absorbs the focus safely; the ContentEditable loses focus
                    // naturally, handleBlur fires, and nothing else activates.
                    const rfRoot = (
                      e.target as HTMLElement
                    ).closest<HTMLElement>(".react-flow");
                    if (rfRoot) {
                      rfRoot.focus({ preventScroll: true });
                    } else {
                      (e.target as HTMLElement).blur();
                    }
                  }
                }}
                onFocus={() => {
                  setIsEditing(true);
                  setIsFocused(true);
                }}
                onBlur={(e) => {
                  setIsFocused(false);
                  handleBlur(e.relatedTarget as HTMLElement | null);
                }}
                style={{
                  cursor: "text",
                  fontFamily,
                  fontSize,
                  fontWeight: isBold ? "bold" : "normal",
                  fontStyle: isItalic ? "italic" : "normal",
                  textDecoration: isUnderline ? "underline" : "none",
                  color: textColorStyle,
                  padding: TEXT_LABEL_TEXT_PADDING,
                }}
              />
            }
            placeholder={
              showPlaceholder ? (
                <div
                  className="absolute top-0 left-0 pointer-events-none"
                  style={{
                    ...promptStyle,
                    color: TEXT_LABEL_PLACEHOLDER_COLOR,
                    padding: TEXT_LABEL_TEXT_PADDING,
                  }}
                >
                  {TEXT_LABEL_PLACEHOLDER}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <DashListPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          <OnChangePlugin
            onChange={handleContentChange}
            ignoreSelectionChange={true}
          />
          <InitializerPlugin
            value={content}
            isFocused={isFocused}
            onInitialized={() => setIsInitialized(true)}
          />
        </LexicalComposer>
      </div>
    </div>
  );
});
